import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { PermissionSet } from '@fint/validation';

export interface OrgSummary {
  id: string;
  name: string;
  slug: string;
  /** A palette token - the book's colour in the switcher, the Overview and the Inbox. */
  colour: string;
  role: 'admin' | 'member';
  roleId: string | null;
  roleName: string;
  /** 'admin' / 'member' / 'auditor' for a built-in role, null for a custom one. */
  roleKey?: string | null;
  /** The organisation the book stands in (0022). */
  organisationId?: string;
  permissions: PermissionSet;
  trialEndsAt: string | null;
  licenseExpiresAt: string | null;
}

/** The shelf a book stands on: named, coloured, owned by one person. */
export interface OrganisationSummary {
  id: string;
  name: string;
  colour: string;
  owner: boolean;
}

export interface AuthUser {
  id: string;
  email: string;
  name: string;
  mfaEnabled: boolean;
  isSuperadmin: boolean;
  /** The AI CFO is offered on this account (staff until its release - docs/roadmap.md). */
  cfo?: boolean;
}

interface AuthState {
  user: AuthUser | null;
  orgs: OrgSummary[];
  organisations: OrganisationSummary[];
  activeOrgId: string | null;
  isAuthenticated: boolean;
  setSession: (user: AuthUser, orgs: OrgSummary[], organisations?: OrganisationSummary[]) => void;
  setActiveOrg: (orgId: string) => void;
  clear: () => void;
}

/**
 * The only store in the app.
 *
 * ── WHAT IS AND IS NOT PERSISTED ──
 * The session itself is an httpOnly cookie the browser holds; NO token is kept
 * here, because anything in localStorage is readable by any script that gets
 * injected onto the page. What is persisted is the chosen org id, so a reload
 * does not dump a multi-org user back into the wrong business - and the user
 * summary, so the shell can render immediately instead of flashing a skeleton
 * while /auth/me resolves.
 *
 * `isAuthenticated` is therefore a HINT, not a fact. The server decides, on
 * every request; this only decides whether to optimistically render the shell.
 */
export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      user: null,
      orgs: [],
      organisations: [],
      activeOrgId: null,
      isAuthenticated: false,

      setSession: (user, orgs, organisations) => {
        const current = get().activeOrgId;
        // Keep the chosen org if it is still one the user belongs to; otherwise
        // fall back to the first. Holding on to an org they were removed from
        // means every request 403s with no way back.
        const activeOrgId = orgs.some((o) => o.id === current) ? current : (orgs[0]?.id ?? null);
        set({ user, orgs, organisations: organisations ?? get().organisations, activeOrgId, isAuthenticated: true });
      },

      setActiveOrg: (orgId) => {
        if (get().orgs.some((o) => o.id === orgId)) set({ activeOrgId: orgId });
      },

      clear: () => set({ user: null, orgs: [], organisations: [], activeOrgId: null, isAuthenticated: false }),
    }),
    {
      name: 'fint-auth',
      partialize: (state) => ({
        user: state.user,
        orgs: state.orgs,
        organisations: state.organisations,
        activeOrgId: state.activeOrgId,
        isAuthenticated: state.isAuthenticated,
      }),
    },
  ),
);

/** The org currently being viewed, or undefined until the profile resolves. */
export const useActiveOrg = (): OrgSummary | undefined => {
  const { orgs, activeOrgId } = useAuthStore();
  return orgs.find((o) => o.id === activeOrgId);
};

/** The organisation the active book stands in, when the session knows it. */
export const useActiveOrganisation = (): OrganisationSummary | undefined => {
  const { orgs, organisations, activeOrgId } = useAuthStore();
  const active = orgs.find((o) => o.id === activeOrgId);
  return organisations.find((o) => o.id === active?.organisationId);
};
